import type { ProductRankingRef } from 'src/types/product-rankings/product-rankings-scope';
import type { ProductRankingItem } from 'src/types/product-rankings/product-rankings-domain';
import type { ProductRankingDetailResponse } from 'src/types/product-rankings/product-rankings-ops';

import { productMallId } from 'src/utils/product-rankings/live-narrow';

import { cacheKey } from './market-rankings-request-cache';

/**
 * **이 화면이 다루는 상품 하나** — 그리고 그것을 어떻게 가리키는가.
 *
 * ## ⚠️ 출처가 둘이라 필드가 다 차 있지 않다
 *
 * ```
 *   랭킹 인덱스        rankings.list · board · search  → ProductRankingItem 전부
 *   판매몰 실시간 검색  /api/products/live-search       → 랭킹 필드 대부분이 빈다
 * ```
 *
 * 둘이 같은 카드·행·상세를 탄다. 그래서 랭킹 쪽 필드를 전부 선택적으로 둔다 — 있는 척하면
 * 화면이 빈 값을 0이나 "없음"으로 그린다.
 */
export type MarketProduct = Partial<ProductRankingItem> & {
  /** 실시간 검색으로 들어온 것 — 몰의 상품 ID */
  liveId?: string;
  /** 실시간 검색 결과가 랭킹과 이어졌는가 */
  ranked?: boolean;
  /** `'id'` | `'title'` — 어떻게 이었나 */
  matchedBy?: string | null;
  hasDetail?: boolean;
  /** 실시간 검색 출신 표시 */
  fromLive?: boolean;
};

/** 목록에서 한 상품을 가리키는 열쇠 — 같은 상품이라도 날짜가 다르면 다른 것이다 */
export function productKey(item: MarketProduct | null | undefined): string {
  return `${item?.source || ''}:${item?.postId || ''}:${item?.date || ''}`;
}

/**
 * 상세 수집 API가 받는 **몰 상품 ID**.
 *
 * ⚠️ 랭킹 인덱스의 `postId`는 올영·무신사·카카오는 몰 ID와 같지만 **화해만
 * `2_2119406`처럼 접두사가 붙어 있어** 떼어내야 한다.
 */
export function mallProductId(product: MarketProduct | null | undefined): string {
  return productMallId(product);
}

/** 수집 API에 넘길 상품 링크. ⚠️ 화해 랭킹 문서의 `link`는 형식이 달라 재구성한다 */
export function mallProductLink(product: MarketProduct | null | undefined): string {
  const id = mallProductId(product);
  if (product?.source === 'hwahae' && id) return `https://www.hwahae.co.kr/products/${id}`;
  return String(product?.link || '');
}

/**
 * 상세 조회가 가리키는 상품 좌표.
 *
 * ⚠️ **`kind: 'search'`는 종류가 아니라 "판매몰 실시간 검색으로 들어왔다"는 표시**라
 * 서버에 넘기지 않는다 — 넘기면 `kind` 필터에 걸려 아무것도 안 나온다.
 *
 * 좌표가 없으면(`postId` 없음) `null`이고 부르는 쪽이 조회를 건너뛴다.
 */
export function detailRefFor(product: MarketProduct | null | undefined): ProductRankingRef | null {
  const postId = String(product?.postId || '');
  if (!postId) return null;
  const kind = String(product?.kind || '');
  return {
    postId,
    source: product?.source || '',
    kind: kind && kind !== 'search' ? kind : '',
  };
}

/** ⚠️ 무효화하는 자리와 읽는 자리가 **같은 키**를 써야 한다 */
export function detailCacheKey(product: MarketProduct | null | undefined): string {
  const ref = detailRefFor(product);
  return ref ? cacheKey('rankings.detail', ref) : '';
}

/**
 * 상세페이지를 이미 긁어 왔는가 — 셋 중 하나라도 있으면 있는 것으로 본다.
 *
 * ⚠️ 이 판정은 **여기 한 곳뿐이다** — 카드·상세 본문·자동 수집이 모두 이 함수를 부른다.
 * 사본을 만들면 한 곳만 고쳐져 조용히 갈린다.
 */
export function hasCollectedDetail(data: unknown): boolean {
  const detail = data as { hasDetail?: unknown; media?: unknown[]; hasTags?: unknown } | null;
  return Boolean(detail?.hasDetail || detail?.media?.length || detail?.hasTags);
}

/**
 * **찾은 상세만** 골라 준다.
 *
 * ⚠️ `ProductRankingDetailResponse`는 `found`로 갈리는 판별 유니온이고, 리뷰·태그·고시정보·
 * 상세페이지는 **찾은 갈래에만 있다.** 못 찾은 갈래에는 `postId`·`source`·`kind`·`hasDetail`
 * 넷뿐이다 — 그대로 읽으려 들면 타입이 막는다.
 */
export function foundDetail(
  data: ProductRankingDetailResponse | null | undefined,
): Extract<ProductRankingDetailResponse, { found: true }> | null {
  return data && data.found ? data : null;
}
